import React, { useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { businessInfo } from '../data/mock';
import { Shield, Mail, Phone } from 'lucide-react';

export const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50 py-20">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Shield className="h-4 w-4 mr-2" />
            Your Privacy Matters
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last Updated: December 2025
          </p>
        </div>

        {/* Content */}
        <Card className="border-2 border-emerald-200 shadow-xl">
          <CardContent className="p-8 md:p-12">
            <div className="prose prose-emerald max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Claim A Trademark ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trademark registration services.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Submit contact forms or request consultations</li>
                  <li>Register for our trademark services</li>
                  <li>Subscribe to newsletters or communications</li>
                  <li>Communicate with us via email, phone, or other channels</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Personal information may include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Full name and contact details (email, phone number, address)</li>
                  <li>Business information (company name, type of business, industry)</li>
                  <li>Trademark information (brand names, logos, descriptions)</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                  <li>Any other information you choose to provide</li>
                </ul>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">2.2 Automatically Collected Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you visit our website, we automatically collect certain information about your device and browsing activities, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website and navigation patterns</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Provide Services:</strong> To process trademark applications, conduct searches, and deliver our legal services</li>
                  <li><strong>Communication:</strong> To respond to inquiries, provide updates, and send service-related notifications</li>
                  <li><strong>Improvement:</strong> To analyze usage patterns and improve our website and services</li>
                  <li><strong>Marketing:</strong> To send promotional materials and newsletters (with your consent)</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
                  <li><strong>Fraud Prevention:</strong> To detect and prevent fraudulent activities</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, rent, or trade your personal information. We may share your information in the following circumstances:
                </p>
                
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">4.1 Service Providers</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may share information with third-party service providers who perform services on our behalf, including payment processors, email service providers, and analytics providers. These providers are contractually obligated to protect your information.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">4.2 USPTO and Legal Authorities</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We share necessary information with the United States Patent and Trademark Office (USPTO) to process your trademark applications. We may also disclose information when required by law or to protect our legal rights.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">4.3 Business Transfers</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">5. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure servers and databases</li>
                  <li>Regular security audits and updates</li>
                  <li>Restricted access to personal information</li>
                  <li>Employee training on data protection</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">6. Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser preferences. However, disabling cookies may limit certain features of our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">7. Your Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Data Portability:</strong> Request a copy of your information in a portable format</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">8. Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete such information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">9. Third-Party Links</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">10. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. Trademark application records may be retained indefinitely for legal and compliance purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">11. International Data Transfers</h2>
                <p className="text-gray-700 leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">13. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                
                <div className="bg-emerald-50 rounded-lg p-6 space-y-3">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-emerald-700 mr-3" />
                    <div>
                      <p className="font-semibold text-emerald-900">Email:</p>
                      <a href={`mailto:${businessInfo.email}`} className="text-emerald-700 hover:text-emerald-800">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-emerald-700 mr-3" />
                    <div>
                      <p className="font-semibold text-emerald-900">Phone:</p>
                      <a href={`tel:${businessInfo.phone}`} className="text-emerald-700 hover:text-emerald-800">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-emerald-700 mr-3 mt-1" />
                    <div>
                      <p className="font-semibold text-emerald-900">Address:</p>
                      <p className="text-gray-700">{businessInfo.address}</p>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </CardContent>
        </Card>

        {/* Back to Home Link */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};
